import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const READING_TIME_FLUSH_INTERVAL_MS = 5000;
const READING_TIME_QUERY_KEYS = [
  "doc-reading-times",
  "doc-reading-times-emp",
  "exec-reading-times",
  "all-doc-reading-times-report",
];

/**
 * Tracks how long a user has a document open.
 * Starts a timer on mount/open, saves cumulative seconds on unmount/close.
 * 
 * @param documentId - UUID of the document being viewed
 * @param documentName - Human-readable name for the record
 * @param source - Where the doc is being read: "onboarding" | "documents" | "sms_review"
 * @param isActive - Whether tracking is currently active (e.g. doc viewer is open)
 */
export function useDocumentReadingTime(
  documentId: string | null,
  documentName: string,
  source: string = "documents",
  isActive: boolean = true
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const startTimeRef = useRef<number | null>(null);
  const accumulatedRef = useRef(0);
  const docIdRef = useRef(documentId);

  const syncElapsedTime = useCallback(() => {
    if (!startTimeRef.current) return;

    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
    if (elapsed > 0) {
      accumulatedRef.current += elapsed;
    }

    startTimeRef.current = Date.now();
  }, []);

  const saveTime = useCallback(async () => {
    if (!user || !docIdRef.current || accumulatedRef.current < 1) return;

    const seconds = accumulatedRef.current;
    const currentDocumentId = docIdRef.current;
    const currentTimestamp = new Date().toISOString();
    accumulatedRef.current = 0;

    try {
      // Try to find existing record
      const { data: existing } = await supabase
        .from("document_reading_time" as any)
        .select("id, total_seconds")
        .eq("user_id", user.id)
        .eq("document_id", currentDocumentId)
        .limit(1);

      if ((existing as any)?.length > 0) {
        const record = (existing as any)[0];
        await supabase
          .from("document_reading_time" as any)
          .update({
            total_seconds: record.total_seconds + seconds,
            last_opened_at: currentTimestamp,
            updated_at: currentTimestamp,
          } as any)
          .eq("id", record.id);
      } else {
        await supabase.from("document_reading_time" as any).insert({
          user_id: user.id,
          document_id: currentDocumentId,
          document_name: documentName,
          source,
          total_seconds: seconds,
          last_opened_at: currentTimestamp,
        } as any);
      }

      await queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          typeof queryKey[0] === "string" && READING_TIME_QUERY_KEYS.includes(queryKey[0]),
      });

      window.dispatchEvent(
        new CustomEvent("reading-time-updated", {
          detail: { documentId: currentDocumentId, userId: user.id, seconds },
        }),
      );
    } catch (e) {
      accumulatedRef.current += seconds;
      console.error("Failed to save reading time", e);
    }
  }, [user, documentName, source, queryClient]);

  // Start/stop timer based on isActive and documentId
  useEffect(() => {
    docIdRef.current = documentId;

    if (isActive && documentId) {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }

      const interval = window.setInterval(() => {
        syncElapsedTime();
        void saveTime();
      }, READING_TIME_FLUSH_INTERVAL_MS);

      return () => {
        window.clearInterval(interval);
        syncElapsedTime();
        startTimeRef.current = null;
        void saveTime();
      };
    }

    if (startTimeRef.current) {
      syncElapsedTime();
      startTimeRef.current = null;
      void saveTime();
    }
  }, [documentId, isActive, saveTime, syncElapsedTime]);

  // Save when the page is backgrounded or closed
  useEffect(() => {
    const flushReadingTime = () => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        if (elapsed > 0) {
          accumulatedRef.current += elapsed;
        }
        startTimeRef.current = null;
      }

      if (accumulatedRef.current > 0) {
        void saveTime();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushReadingTime();
        return;
      }

      if (isActive && docIdRef.current && !startTimeRef.current) {
        startTimeRef.current = Date.now();
      }
    };

    window.addEventListener("pagehide", flushReadingTime);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", flushReadingTime);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isActive, saveTime]);
}

/** Format seconds into human-readable string */
export function formatReadingTime(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}
